import {
	createRootRoute,
	createRoute,
	createRouter,
	Navigate,
	Outlet,
} from "@tanstack/react-router";
import { LoaderCircle } from "lucide-react";
import { z } from "zod";
import ProtectedRoute from "@/src/lib/components/app/ProtectedRoute";
import { withPageErrorBoundary } from "@/src/lib/components/errors/PageErrorBoundary";
import { useAnalytics } from "../lib/hooks/use-analytics";
import { useProtectedRoute } from "../lib/hooks/use-protected-route";
import AdminAdmins from "./admin/admins";
// Admin imports
import AdminDashboard from "./admin/dashboard";
import AdminMenu from "./admin/menu";
import AdminOrderDetails from "./admin/order-details";
import AdminOrders from "./admin/orders";
import AdminUsers from "./admin/users";
import ForgotPassword from "./auth/forgot-password";
import ResetPasswordParamRoute, {
	ResetPasswordQueryRoute,
} from "./auth/reset-password";
import Signin from "./auth/signin";
// Auth imports
import Signup from "./auth/signup";
import VerifyEmail from "./auth/verify-email";
import Cart from "./cart";
import Checkout from "./checkout";
import Confirmation from "./confirmation";
import Home from "./home";
import Privacy from "./legal/privacy";
import Terms from "./legal/terms";
import Orders from "./orders";
import Profile from "./profile";
import Addresses from "./profile/addresses";
import Refund from "./refund";
import Review from "./review";
import Settings from "./settings";
import Support from "./support";

// Home component that redirects admins to admin dashboard
function HomePage() {
	const { userRole, isLoading } = useProtectedRoute();

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-screen">
				<LoaderCircle className="size-10 animate-spin" />
			</div>
		);
	}

	if (userRole === "admin") {
		return <Navigate to="/admin" />;
	}

	return withPageErrorBoundary(Home)({});
}

const rootRoute = createRootRoute({
	component: () => {
		useAnalytics();

		return <Outlet />;
	},
});

const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	component: HomePage,
});

const cartRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/cart",
	component: function CartPage() {
		return (
			<ProtectedRoute
				requiredRole="user"
				requireVerified
				redirectTo="/auth/signin"
			>
				{withPageErrorBoundary(Cart)({})}
			</ProtectedRoute>
		);
	},
});

const checkoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/checkout",
	component: function CheckoutPage() {
		return (
			<ProtectedRoute
				requiredRole="user"
				requireVerified
				redirectTo="/auth/signin"
			>
				{withPageErrorBoundary(Checkout)({})}
			</ProtectedRoute>
		);
	},
});

const reviewRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/review",
	component: function ReviewPage() {
		return (
			<ProtectedRoute requiredRole="user" redirectTo="/auth/signin">
				{withPageErrorBoundary(Review)({})}
			</ProtectedRoute>
		);
	},
});

const confirmationRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/confirmation",
	validateSearch: (search: Record<string, unknown>) =>
		z
			.object({
				session_id: z.string().optional().catch(undefined),
			})
			.parse(search),
	component: function ConfirmationPage() {
		return (
			<ProtectedRoute requiredRole="user" redirectTo="/auth/signin">
				{withPageErrorBoundary(Confirmation)({})}
			</ProtectedRoute>
		);
	},
});

const supportRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/support",
	component: function SupportPage() {
		return (
			<ProtectedRoute requiredRole="user" redirectTo="/auth/signin">
				{withPageErrorBoundary(Support)({})}
			</ProtectedRoute>
		);
	},
});

const profileRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/profile",
	component: function ProfilePage() {
		return (
			<ProtectedRoute requiredRole="user" redirectTo="/auth/signin">
				{withPageErrorBoundary(Profile)({})}
			</ProtectedRoute>
		);
	},
});

const profileAddressesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/profile/addresses",
	component: function ProfileAddressesPage() {
		return (
			<ProtectedRoute requiredRole="user" redirectTo="/auth/signin">
				{withPageErrorBoundary(Addresses)({})}
			</ProtectedRoute>
		);
	},
});

// const notificationsRoute = createRoute({
//   getParentRoute: () => rootRoute,
//   path: '/notifications',
//   component: function NotificationsPage() {
//     return (
//       <ProtectedRoute requiredRole="user" redirectTo="/auth/signin">
//         {withPageErrorBoundary(Notifications)({})}
//       </ProtectedRoute>
//     );
//   },
// })

const settingsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/settings",
	component: function SettingsPage() {
		return (
			<ProtectedRoute requiredRole="user" redirectTo="/auth/signin">
				{withPageErrorBoundary(Settings)({})}
			</ProtectedRoute>
		);
	},
});

const ordersRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/orders",
	component: function OrdersPage() {
		return (
			<ProtectedRoute
				requiredRole="user"
				requireVerified
				redirectTo="/auth/signin"
			>
				{withPageErrorBoundary(Orders)({})}
			</ProtectedRoute>
		);
	},
});

const refundRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/refund",
	validateSearch: (search: Record<string, unknown>) =>
		z
			.object({
				orderId: z.coerce.number().optional().catch(undefined),
			})
			.parse(search),
	component: function RefundPage() {
		return (
			<ProtectedRoute requiredRole="user" redirectTo="/auth/signin">
				{withPageErrorBoundary(Refund)({})}
			</ProtectedRoute>
		);
	},
});

const privacyRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/legal/privacy",
	component: function PrivacyPage() {
		return withPageErrorBoundary(Privacy)({});
	},
});

const termsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/legal/terms",
	component: function TermsPage() {
		return withPageErrorBoundary(Terms)({});
	},
});

// Auth routes
const signupRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/auth/signup",
	component: function SignupPage() {
		return withPageErrorBoundary(Signup)({});
	},
});

const signinRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/auth/signin",
	component: function SigninPage() {
		return withPageErrorBoundary(Signin)({});
	},
});

const forgotPasswordRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/auth/forgot-password",
	component: function ForgotPasswordPage() {
		return withPageErrorBoundary(ForgotPassword)({});
	},
});

const resetPasswordRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/auth/reset-password/$token",
	component: function ResetPasswordPage() {
		return withPageErrorBoundary(ResetPasswordParamRoute)({});
	},
});

const resetPasswordQueryRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/auth/reset-password",
	component: function ResetPasswordQueryPage() {
		return withPageErrorBoundary(ResetPasswordQueryRoute)({});
	},
});

const verifyEmailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/auth/verify-email",
	component: function VerifyEmailPage() {
		return withPageErrorBoundary(VerifyEmail)({});
	},
});

// Admin layout route (protects all /admin/* routes)
const adminLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/admin",
	component: function AdminLayout() {
		return (
			<ProtectedRoute requiredRole="admin" redirectTo="/">
				<Outlet />
			</ProtectedRoute>
		);
	},
});

// Admin routes (no longer need individual protection)
const adminDashboardRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/",
	component: function AdminDashboardPage() {
		return withPageErrorBoundary(AdminDashboard)({});
	},
});

const adminOrdersRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/orders",
	component: function AdminOrdersPage() {
		return withPageErrorBoundary(AdminOrders)({});
	},
});

const adminOrderDetailsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/orders/$orderId",
	component: function AdminOrderDetailsPage() {
		return withPageErrorBoundary(AdminOrderDetails)({});
	},
});

const adminMenuRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/menu",
	component: function AdminMenuPage() {
		return withPageErrorBoundary(AdminMenu)({});
	},
});

const adminUsersRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/users",
	component: function AdminUsersPage() {
		return withPageErrorBoundary(AdminUsers)({});
	},
});

const adminAdminsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admins",
	component: function AdminAdminsPage() {
		return withPageErrorBoundary(AdminAdmins)({});
	},
});

const routeTree = rootRoute.addChildren([
	indexRoute,
	cartRoute,
	checkoutRoute,
	reviewRoute,
	confirmationRoute,
	supportRoute,
	profileRoute,
	profileAddressesRoute,
	// notificationsRoute,
	settingsRoute,
	ordersRoute,
	refundRoute,
	// Legal routes
	privacyRoute,
	termsRoute,
	// Auth routes
	signupRoute,
	signinRoute,
	forgotPasswordRoute,
	resetPasswordRoute,
	resetPasswordQueryRoute,
	verifyEmailRoute,
	// Admin routes (all protected by adminLayoutRoute)
	adminLayoutRoute.addChildren([
		adminDashboardRoute,
		adminOrdersRoute,
		adminOrderDetailsRoute,
		adminMenuRoute,
		adminUsersRoute,
		adminAdminsRoute,
	]),
]);
const router = createRouter({
	routeTree,
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

export default router;
